/**
 * Shared GreenCharge wordmark.
 *
 * Two source assets ship in /public — one designed to sit on light
 * backgrounds (greencharge-0.png; dark ink) and one designed for dark
 * backgrounds (greencharge-1.png; light ink). This component reads
 * the resolved theme (via ThemeContext, with a graceful fallback to
 * the current `data-theme` attribute on <html>) and picks the right
 * asset automatically. Callers do not need to know either path.
 *
 * Props:
 *   - variant   : 'wordmark' | 'centered'. Presets for the two most
 *                 common placements (page headers vs terminal cards).
 *   - alt       : accessible alt text (default: 'GreenCharge').
 *   - className : optional className for layout hooks.
 *   - width     : optional numeric width — pass to lock a layout.
 *   - height    : optional numeric height — overrides the variant
 *                 default when provided.
 *   - style     : optional inline style overrides that layer on top
 *                 of the variant defaults so existing call sites that
 *                 tweak height/maxWidth keep working unchanged.
 */

import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../theme/theme.jsx';

const LIGHT_SRC = '/greencharge-0.png';   // dark ink on light background
const DARK_SRC  = '/greencharge-1.png';   // light ink on dark background

const SIZES = {
  wordmark: { height: 36, maxWidth: 140 },
  centered: { height: 56, maxWidth: 200 },
};

/**
 * When no ThemeProvider is in scope (some legacy routes still mount
 * without one), fall back to observing <html data-theme> directly.
 * The pre-hydration script in index.html sets this attribute BEFORE
 * React boots, so a fresh reload paints with the correct asset.
 */
function useResolvedTheme() {
  const ctx = useContext(ThemeContext);
  const [attr, setAttr] = useState(() =>
    (typeof document !== 'undefined'
      && document.documentElement.getAttribute('data-theme')) || 'dark'
  );
  useEffect(() => {
    if (ctx) return undefined; // provider already tells us the state
    if (typeof document === 'undefined') return undefined;
    const el = document.documentElement;
    const mo = new MutationObserver(() => {
      setAttr(el.getAttribute('data-theme') || 'dark');
    });
    mo.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, [ctx]);
  return ctx ? ctx.resolved : attr;
}

function GreenChargeLogo({
  variant = 'wordmark',
  alt = 'GreenCharge',
  className,
  width,
  height,
  style,
}) {
  const resolved = useResolvedTheme();
  const dims = SIZES[variant] || SIZES.wordmark;
  const src = resolved === 'light' ? LIGHT_SRC : DARK_SRC;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={{
        // Height wins; width auto keeps the aspect ratio intact so
        // the asset never stretches inside a flex row.
        height: height ?? dims.height,
        width: width ?? 'auto',
        maxWidth: dims.maxWidth,
        objectFit: 'contain',
        display: 'block',
        flexShrink: 0,
        ...style,
      }}
    />
  );
}

export default GreenChargeLogo;
